import { readApiJson } from '../utils/readApiJson';

const API_BASE_URL = '/api/prisme';

export interface DocumentRecord {
  id: number;
  client_id: number;
  document_type_code: string;
  besoin_id?: number | null;
  quality_level: number;
  title?: string | null;
  content: any;
  status: string;
  showpriseclient: boolean;
  presented_client: boolean;
  archived: boolean;
  metadata?: any;
  approved_dany?: number | null;
  approved_specialist?: number | null;
  comments?: string | null;
  created_at?: string;
  updated_at?: string;
  created_by?: number | null;
  last_modified_by?: number | null;
}

export async function getDocumentsByClient(clientId: number): Promise<DocumentRecord[]> {
  const response = await fetch(`${API_BASE_URL}/documents/${clientId}`, { credentials: 'include' });
  const data = await readApiJson<DocumentRecord[] | { error?: string }>(response);
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || 'Erreur lors de la récupération des documents');
  }
  return data as DocumentRecord[];
}

export async function getAllDocuments(includeArchived = false): Promise<DocumentRecord[]> {
  const url = `${API_BASE_URL}/documents${includeArchived ? '?includeArchived=true' : ''}`;
  const response = await fetch(url, { credentials: 'include' });
  const data = await readApiJson<DocumentRecord[] | { error?: string }>(response);
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || 'Erreur lors de la récupération des documents');
  }
  return data as DocumentRecord[];
}

export async function getDocumentById(documentId: number): Promise<DocumentRecord | null> {
  const response = await fetch(`${API_BASE_URL}/document/${documentId}`, { credentials: 'include' });
  if (response.status === 404) {
    await response.text();
    return null;
  }
  const data = await readApiJson<DocumentRecord | { error?: string }>(response);
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || 'Erreur lors de la récupération du document');
  }
  return data as DocumentRecord;
}

export async function updateDocument(
  documentId: number,
  data: Partial<DocumentRecord> & { commentaire?: string; modified_by?: number }
): Promise<DocumentRecord> {
  const response = await fetch(`${API_BASE_URL}/document/${documentId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  const result = await readApiJson<any>(response);
  if (!response.ok) {
    throw new Error(result?.error || 'Erreur lors de la mise à jour du document');
  }
  return result.data || result;
}

export async function createDocument(
  data: Omit<DocumentRecord, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'last_modified_by'> & {
    created_by?: number | null;
  }
): Promise<DocumentRecord> {
  const response = await fetch(`${API_BASE_URL}/document`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  });

  const result = await readApiJson<any>(response);
  if (!response.ok) {
    throw new Error(result?.error || 'Erreur lors de la création du document');
  }
  return result.data || result;
}

export async function finalizeClient2026AnalysisDocument(
  jobId: string,
  besoinId: number,
): Promise<DocumentRecord> {
  const response = await fetch(`${API_BASE_URL}/documents/client2026-analysis/finalize`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ jobId, besoinId }),
  });
  const result = await readApiJson<{ data?: DocumentRecord; error?: string }>(response);
  if (!response.ok || !result?.data) {
    throw new Error(result?.error || 'Impossible de finaliser le rapport Client 2026');
  }
  return result.data;
}

export async function deleteDocument(documentId: number, deletedBy?: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/document/${documentId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ deleted_by: deletedBy }),
  });

  const errBody = await readApiJson<{ error?: string } | undefined>(response);
  if (!response.ok) {
    throw new Error(errBody?.error || 'Erreur lors de la suppression du document');
  }
}

export async function getDocumentHistorique(documentId: number): Promise<any[]> {
  const response = await fetch(`${API_BASE_URL}/document/${documentId}/historique`, { credentials: 'include' });
  const data = await readApiJson<any[] | { error?: string }>(response);
  if (!response.ok) {
    throw new Error((data as { error?: string })?.error || "Erreur lors de la récupération de l'historique du document");
  }
  return data as any[];
}
